import { Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import IconButton from '@components/IconButton';
import RoundedButton from '@components/RoundedButton';

import { useIsMobile } from '@hooks/useIsMobile';

import PatientPlanMobileRow from './MobileTableRow';

const messages = defineMessages({
  title: {
    id: 'patientPlanTable.title',
    defaultMessage: 'Patient Plans',
  },
  noData: {
    id: 'patientPlanTable.noData',
    defaultMessage: 'No Patient Plans',
  },
  button: {
    id: 'patientPlanTable.add',
    defaultMessage: 'Add patient plan type',
  },
  'column.active': {
    id: 'patientPlanTable.column.active',
    defaultMessage: 'Active',
  },
  'column.invited': {
    id: 'patientPlanTable.column.invited',
    defaultMessage: 'Invited',
  },
  'column.new': {
    id: 'patientPlanTable.column.new',
    defaultMessage: 'New',
  },
  'column.plan': {
    id: 'patientPlanTable.column.plan',
    defaultMessage: 'Plan',
  },
  'column.purchased': {
    id: 'orgSettings.licensePlan.column.seats',
    defaultMessage: 'Seats',
  },
  'column.totalAssigned': {
    id: 'patientPlanTable.column.totalAssigned',
    defaultMessage: 'Total Assigned',
  },
  'column.edit': {
    id: 'patientPlanTable.column.edit',
    defaultMessage: 'Edit',
  },
  'total.patients': {
    id: 'patientPlanTable.total.patients',
    defaultMessage: 'Total Patients',
  },
  'total.plans': {
    id: 'orgSettings.licensePlan.totalSeatsAllocated',
    defaultMessage: 'Total Seats Allocated',
  },
  'total.ofSeats': {
    id: 'patientPlanTable.total.ofSeats',
    defaultMessage: 'of {total} seats',
  },
  planSummary: {
    id: 'patientPlanTable.planSummary',
    defaultMessage:
      '{selected} of {total} patient features · {percent}% of seats',
  },
  unnamedPlan: {
    id: 'patientPlans.unnamedPlan',
    defaultMessage: 'Plan {number}',
  },
});

const LIMIT = 10;

/**
 * One row of the org's patient plans. Seats and feature counts come from the
 * editable form state; status counts from the purchased plans, joined on
 * `plan_id`.
 */
export type PlanRowData = {
  /** Stable key from the form state, since a new plan has no plan_id yet */
  local_id: string;
  name: string;
  /** Seats reserved for this plan out of the org's pool */
  seats: number;
  /** Patient features this plan includes, of those the org has enabled */
  features_selected: number;
  features_total: number;
  /** Share of the org's seat pool this plan reserves; null when no pool is set */
  seats_percent: number | null;
  new_users: number;
  invited_users: number;
  active_users: number;
  total_assigned: number;
};

interface PatientPlanTableProps {
  data: PlanRowData[];
  /** Seats reserved across every plan */
  allocated: number;
  /** The org's seat pool; null until one is configured */
  totalSeats?: number | null;
  /** Patients assigned across every plan */
  patientCount: number;
  onEdit: (rowIndex: number) => void;
  onAdd: () => void;
  disabled?: boolean;
}

export default function PatientPlanTable({
  data,
  allocated,
  totalSeats,
  patientCount,
  onEdit,
  onAdd,
  disabled,
}: PatientPlanTableProps) {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile('md');

  return (
    <MuiDataTable
      columns={[
        {
          name: 'name',
          label: formatMessage(messages['column.plan']),
          options: {
            customBodyRender: (name: PlanRowData['name'], { rowIndex }) => {
              const row = data[rowIndex];
              return (
                <Stack>
                  <Typography variant="body2">
                    {name ||
                      formatMessage(messages.unnamedPlan, {
                        number: rowIndex + 1,
                      })}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    data-testid={`patient-plan-row-summary-${rowIndex}`}
                    variant="caption"
                  >
                    {formatMessage(messages.planSummary, {
                      percent: row.seats_percent ?? 0,
                      selected: row.features_selected,
                      total: row.features_total,
                    })}
                  </Typography>
                </Stack>
              );
            },
          },
        },
        {
          name: 'seats',
          label: formatMessage(messages['column.purchased']),
          options: {
            customBodyRender: (seats: PlanRowData['seats']) => seats || 0,
          },
        },
        {
          name: 'new_users',
          label: formatMessage(messages['column.new']),
          options: {
            customBodyRender: (count: PlanRowData['new_users']) => count || 0,
          },
        },
        {
          name: 'invited_users',
          label: formatMessage(messages['column.invited']),
          options: {
            customBodyRender: (count: PlanRowData['invited_users']) =>
              count || 0,
          },
        },
        {
          name: 'active_users',
          label: formatMessage(messages['column.active']),
          options: {
            customBodyRender: (count: PlanRowData['active_users']) =>
              count || 0,
          },
        },
        {
          name: 'total_assigned',
          label: formatMessage(messages['column.totalAssigned']),
          options: {
            customBodyRender: (count: PlanRowData['total_assigned']) =>
              count || 0,
          },
        },
        {
          name: 'local_id',
          label: formatMessage(messages['column.edit']),
          options: {
            sort: false,
            customHeadLabelRender: () => null,
            customBodyRender: (_local_id: string, { rowIndex }) => (
              <IconButton
                disabled={disabled}
                iconButtonProps={{
                  // The icon is aria-hidden and the tooltip is not announced,
                  // so the button carries its own label.
                  'aria-label': `${formatMessage(messages['column.edit'])} ${
                    data[rowIndex].name
                  }`,
                }}
                iconType="fas"
                name="pencil"
                onClick={() => onEdit(rowIndex)}
                sx={{ '&&': { padding: 1 } }}
                tooltip={formatMessage(messages['column.edit'])}
              />
            ),
          },
        },
      ]}
      data={data}
      options={{
        textLabels: {
          body: {
            noMatch: formatMessage(messages.noData),
          },
        },
        rowsPerPage: LIMIT,
        ...(isMobile
          ? {
              customRowRender: (data: React.ReactNode[], dataIndex: number) => (
                <PatientPlanMobileRow
                  count={data.length}
                  data={data}
                  dataIndex={dataIndex}
                />
              ),
            }
          : { customRowRender: undefined }),
        customFooter: () => {
          return (
            <>
              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                justifyContent={{
                  xs: 'flex-start',
                  sm: 'space-between',
                }}
                pt={0.5}
                px={1}
              >
                <Stack direction="row" alignItems={'center'} columnGap={0.5}>
                  <Typography>
                    {formatMessage(messages['total.plans'])}
                  </Typography>
                  <Typography
                    data-testid="plans-total-allocated"
                    variant="subtitle2"
                  >
                    {allocated}
                  </Typography>
                  {typeof totalSeats === 'number' && (
                    <Typography color="text.secondary" variant="caption">
                      {formatMessage(messages['total.ofSeats'], {
                        total: totalSeats,
                      })}
                    </Typography>
                  )}
                </Stack>

                <Stack direction="row" alignItems={'center'} columnGap={0.5}>
                  <Typography>
                    {formatMessage(messages['total.patients'])}
                  </Typography>
                  <Typography
                    data-testid="plans-total-patients"
                    variant="subtitle2"
                  >
                    {patientCount}
                  </Typography>
                </Stack>
              </Stack>
            </>
          );
        },
      }}
      title={
        <Stack alignItems="center" direction="row" columnGap={1}>
          <Typography>{formatMessage(messages.title)}</Typography>
          <RoundedButton
            autoSize
            disabled={disabled}
            height={32}
            margin={0}
            onClick={onAdd}
          >
            {formatMessage(messages.button)}
          </RoundedButton>
        </Stack>
      }
    />
  );
}
